/**
 * Pricing Management API Hooks
 * React hooks for managing base pricing data
 */

import { useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { pricingApiClient } from './pricingApiClient';
import {
  BasePricing,
  BasePricingDTO,
  VehicleCategory,
  CityMaster,
  PricingListQueryParams,
  ApiResponse,
  PagedData
} from './pricingTypes';

/**
 * Hook to fetch a single pricing by ID
 * @param id - Pricing ID
 * @param options - Hook options
 */
export function usePricing(
  id: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: BasePricing) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await pricingApiClient.get<ApiResponse<BasePricing>>(`/base-pricing/${id}`);

    // Handle Response wrapper - the actual pricing data is in response.data.data
    // But also handle case where response might be already unwrapped
    if (response.data?.data) {
      return response.data.data;
    }

    // Fallback for different response structures
    if (response.data) {
      // Check if response.data already contains the BasePricing object directly
      if ('pricingId' in response.data) {
        return response.data as BasePricing;
      }
    }

    throw new Error('Unable to parse pricing data from API response');
  }, [id]);

  return useQuery<BasePricing>({
    queryKey: ['pricing', id], // Query key
    queryFn, // Query function
    enabled: options.enabled !== false && !!id,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch paginated list of pricings
 * @param params - Query parameters (page, size, sort, search)
 * @param options - Hook options
 */
export function usePricings(
  params: PricingListQueryParams = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: PagedData<BasePricing>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};

    if (params.page !== undefined) {
      queryParams.page = params.page;
    }
    if (params.size !== undefined) {
      queryParams.size = params.size;
    }
    if (params.sort && params.sort.length > 0) {
      queryParams.sort = params.sort;
    }
    if (params.search) {
      queryParams.search = params.search;
    }
    if (params.vehicleCategoryId) {
      queryParams.vehicleCategoryId = params.vehicleCategoryId;
    }
    if (params.cityId) {
      queryParams.cityId = params.cityId;
    }
    if (params.isActive !== undefined) {
      queryParams.isActive = params.isActive;
    }

    // For GET request with pagination, we need to send pageable in query params
    const response = await pricingApiClient.get<ApiResponse<PagedData<BasePricing>>>(
      '/base-pricing/list',
      queryParams
    );

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as PagedData<BasePricing>;
  }, [params.page, params.size, params.sort, params.search, params.vehicleCategoryId, params.cityId, params.isActive]);

  return useQuery<PagedData<BasePricing>>({
    queryKey: ['pricings', params], // Query key
    queryFn, // Query function
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch all pricings (no pagination)
 * @param options - Hook options
 */
export function useAllPricings(
  options: {
    enabled?: boolean;
    onSuccess?: (data: BasePricing[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await pricingApiClient.get<ApiResponse<BasePricing[]>>(`/base-pricing/list-only`);

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as BasePricing[];
  }, []);

  return useQuery<BasePricing[]>({
    queryKey: ['all-pricings'], // Query key
    queryFn, // Query function
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to create a new pricing
 * @param options - Hook options
 */
export function useCreatePricing(
  options: {
    onSuccess?: (data: ApiResponse<BasePricing>, variables: BasePricingDTO) => void;
    onError?: (error: Error, variables: BasePricingDTO) => void;
  } = {}
) {
  return useMutation<ApiResponse<BasePricing>, Error, BasePricingDTO>({
    mutationFn: async (pricingData: BasePricingDTO) => {
      const response = await pricingApiClient.post<ApiResponse<BasePricing>>(
        '/base-pricing',
        pricingData
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to update an existing pricing
 * @param options - Hook options
 */
export function useUpdatePricing(
  options: {
    onSuccess?: (
      data: ApiResponse<BasePricing>,
      variables: { id: number; data: BasePricingDTO }
    ) => void;
    onError?: (error: Error, variables: { id: number; data: BasePricingDTO }) => void;
  } = {}
) {
  return useMutation<ApiResponse<BasePricing>, Error, { id: number; data: BasePricingDTO }>({
    mutationFn: async ({ id, data }: { id: number; data: BasePricingDTO }) => {
      const response = await pricingApiClient.put<ApiResponse<BasePricing>>(
        `/base-pricing/${id}`,
        data
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to delete a pricing
 * @param options - Hook options
 */
export function useDeletePricing(
  options: {
    onSuccess?: (data: ApiResponse<void>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  return useMutation<ApiResponse<void>, Error, number>({
    mutationFn: async (id: number) => {
      const response = await pricingApiClient.delete<ApiResponse<void>>(`/base-pricing/${id}`);
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to toggle pricing active status
 * @param options - Hook options
 */
export function useTogglePricingStatus(
  options: {
    onSuccess?: (data: ApiResponse<BasePricing>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  return useMutation<ApiResponse<BasePricing>, Error, number>({
    mutationFn: async (id: number) => {
      const response = await pricingApiClient.get<ApiResponse<BasePricing>>(
        `/base-pricing/toggle-active-status/${id}`
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch vehicle categories
 * @param options - Hook options
 */
export function useVehicleCategories(
  options: {
    enabled?: boolean;
    onSuccess?: (data: VehicleCategory[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await pricingApiClient.get<ApiResponse<VehicleCategory[]>>(
      `/vehicle-category/active-list-only`
    );

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as VehicleCategory[];
  }, []);

  return useQuery<VehicleCategory[]>({
    queryKey: ['vehicle-categories'], // Query key
    queryFn, // Query function
    staleTime: 0, // Always refetch to pick up changes from vehicle category management
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch a single city by ID (returns complete details with nested state/country)
 * @param id - City ID
 * @param options - Hook options
 */
export function useCity(
  id: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: CityMaster) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    const response = await pricingApiClient.get<ApiResponse<CityMaster>>(
      `/city/${id}`
    );

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as CityMaster;
  }, [id]);

  return useQuery<CityMaster>({
    queryKey: ['city', id],
    queryFn,
    enabled: options.enabled !== false && !!id,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch cities
 * @param options - Hook options
 */
export function useCities(
  options: {
    enabled?: boolean;
    onSuccess?: (data: CityMaster[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await pricingApiClient.get<ApiResponse<CityMaster[]>>(
      `/city/list-only`
    );

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response.data as CityMaster[];
  }, []);

  return useQuery<CityMaster[]>({
    queryKey: ['cities'], // Query key
    queryFn, // Query function
    staleTime: 0, // Always refetch to pick up changes from city management
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}